import styled from 'styled-components';
import { type FC, useMemo, useState } from 'react';
import { Card } from '../Card';
import { useSecuritySnapshotQuery } from '../../apiHooks/useSecuritySnapshotQuery';
import { useMeQuery } from '../../apiHooks/useMeQuery';
import { type CyberIQLevel, useCyberIQLevelsQuery } from '../../apiHooks/useCyberIQLevelsQuery';
import type { SnapshotLevel } from '../../contentEntryRefs/contentEntryRefs';
import { NamedContentEntryText } from '../ContentEntryText/NamedContentEntryText';
import { formatNumberWithCommas } from '../../lib/numbers/formatNumberWithComma';
import { useAvatarQuery } from '../../apiHooks/useAvatarQuery';
import { useLeaderboardQuery, LeaderboardTypeParam } from '../../apiHooks/useLeaderboardQuery';
import { AvatarInitials } from '../AvatarInitials';
import { RobotAvatar } from '../Props/RobotAvatar/RobotAvatar';
import {
  SteppingProgressBar,
  type SteppingProgressBarStep,
} from '../ProgressBar/SteppingProgressBar/SteppingProgressBar';
import { Icon, IconType } from '../Props/Icon/Icon';
import type { LeaderboardUser } from '../../lib/types/LeaderboardUser';
import { useScoreBreakdownByUserQuery } from '../../apiHooks/useScoreBreakdownByUserQuery';
import { Tooltip } from '../Tooltip';
import { LeaderboardModal } from '../Leaderboard/LeaderboardModal';
import ExternalLinkButton from '../Button/ExternalLinkButton/ExternalLinkButton';
import { UserLevelDetails } from '../../lib/enums/userLevels';

export const SecuritySnapshotUI: FC = () => {
  const { data: securitySnapshotData } = useSecuritySnapshotQuery();
  const { data: cyberIqLevelsData } = useCyberIQLevelsQuery();
  const { data: meData } = useMeQuery();
  const { data: avatarData } = useAvatarQuery();
  const { data: scoreBreakdownData } = useScoreBreakdownByUserQuery();
  const { data: teamLeaderboardData } = useLeaderboardQuery({
    type: LeaderboardTypeParam.TEAM,
    mode: 'top',
    count: 100,
  });
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  const currentLevelIndex: number = useMemo(
    () =>
      cyberIqLevelsData.findIndex((level) => {
        return level.id === securitySnapshotData.levelId;
      }),
    [cyberIqLevelsData, securitySnapshotData.levelId],
  );
  const stepperSteps: SteppingProgressBarStep[] = useMemo(
    () =>
      cyberIqLevelsData.map((level, index) => ({
        value: index,
        label: level.id,
      })),
    [cyberIqLevelsData],
  );

  const dataLevel: CyberIQLevel | undefined = cyberIqLevelsData[currentLevelIndex];
  const currentLevelLabel: SnapshotLevel | undefined = dataLevel?.id;
  const levelColor: string | undefined =
    currentLevelIndex >= 0 ? Object.values(UserLevelDetails)[currentLevelIndex]?.color : undefined;
  const securitySnapshotLevelKey = currentLevelLabel
    ? (`securitySnapshotLevels${currentLevelLabel}` as const)
    : undefined;
  const teamLeaderboardEntries = useMemo<LeaderboardUser[]>(() => {
    const entries = Array.isArray(teamLeaderboardData) ? teamLeaderboardData : teamLeaderboardData.values;

    return entries.filter((entry): entry is LeaderboardUser => 'learnerId' in entry);
  }, [teamLeaderboardData]);
  const currentUserLeaderboardEntry = useMemo(
    () => teamLeaderboardEntries.find((entry) => entry.learnerId === securitySnapshotData.learnerId),
    [teamLeaderboardEntries, securitySnapshotData.learnerId],
  );
  const profileSubtitle = meData?.department || null;
  const monthlyScoreDelta = useMemo(() => {
    const baseScore = scoreBreakdownData?.scoreBreakdownAggregations.reduce((sum, item) => sum + item.score, 0) ?? 0;
    const dynamicScore = scoreBreakdownData?.dynamicActionAggregations?.reduce((sum, item) => sum + item.score, 0) ?? 0;

    return baseScore + dynamicScore;
  }, [scoreBreakdownData]);
  const monthlyScoreLabel =
    monthlyScoreDelta > 0 ? `+${formatNumberWithCommas(monthlyScoreDelta)}` : formatNumberWithCommas(monthlyScoreDelta);

  return (
    <>
      <HeroLayout>
        <AvatarCard>
          <AvatarFrame>
            <RobotAvatar levelId={currentLevelLabel} fillContainer />
          </AvatarFrame>
        </AvatarCard>

        <StatsCard>
          <ProfileHeader>
            <ProfileIdentity>
              {avatarData?.avatar ? (
                <ProfileAvatar src={`data:image/png;base64,${avatarData.avatar}`} alt={meData?.name ?? 'User avatar'} />
              ) : (
                <AvatarInitials name={meData?.name || ''} id={meData?.learnerId ?? ''} size="48px" />
              )}
              <Identity>
                <HeaderTopRow>
                  <ProfileName>{meData?.name}</ProfileName>
                  {securitySnapshotLevelKey && levelColor ? (
                    <LevelPill $color={levelColor}>
                      <NamedContentEntryText container={'span'} refKey={securitySnapshotLevelKey} subKey="levelN" />
                    </LevelPill>
                  ) : null}
                  <MonthlyScore>
                    <Icon icon={IconType.COIN} width={16} height={16} />
                    <span>{monthlyScoreLabel}</span>
                    <span>this month</span>
                  </MonthlyScore>
                </HeaderTopRow>
                {profileSubtitle ? <ProfileSubtitle>{profileSubtitle}</ProfileSubtitle> : null}
              </Identity>
            </ProfileIdentity>
          </ProfileHeader>

          <MetricsGrid>
            <Tooltip fullWidth placement="top" content={<TooltipInner>Cyber Quotient points</TooltipInner>}>
              <MetricCard>
                <MetricIcon $color="#ffb547">
                  <Icon icon={IconType.COIN} width={28} height={28} />
                </MetricIcon>
                <MetricContent>
                  <MetricLabel>CQ points</MetricLabel>
                  <MetricValue>{formatNumberWithCommas(securitySnapshotData.cyberIqPoints)}</MetricValue>
                </MetricContent>
              </MetricCard>
            </Tooltip>
            <Tooltip
              fullWidth
              interactive
              placement="top"
              content={
                <TooltipInner>
                  <span>Place in the leaderboard</span>
                  <TooltipActionButton type="button" onClick={() => setShowLeaderboard(true)}>
                    View more
                  </TooltipActionButton>
                </TooltipInner>
              }
            >
              <MetricCard>
                <MetricIcon $color="#4c5aff">
                  <Icon icon={IconType.PROGRESS_ARROW_UP} width={28} height={28} />
                </MetricIcon>
                <MetricContent>
                  <MetricLabel>Ranking</MetricLabel>
                  <MetricValue>
                    {currentUserLeaderboardEntry ? `#${currentUserLeaderboardEntry.rank}` : '-'}
                  </MetricValue>
                </MetricContent>
              </MetricCard>
            </Tooltip>
            <Tooltip
              fullWidth
              interactive
              placement="top"
              content={
                <TooltipInner>
                  <span>Badges collected</span>
                  <ExternalLinkButton
                    href="https://help.outthink.io/earn-badges/"
                    text={<NamedContentEntryText container={'span'} refKey="leaderboard" subKey="howToEarn" />}
                    hideIcon
                  />
                </TooltipInner>
              }
            >
              <MetricCard>
                <MetricIcon $color="#a66b00">
                  <BadgeHexIcon aria-hidden="true" viewBox="0 0 28 28">
                    <path d="M9 3.5H19L24.5 9V19L19 24.5H9L3.5 19V9L9 3.5Z" />
                    <path d="M10.75 8.75H17.25V19.25H10.75V8.75Z" />
                  </BadgeHexIcon>
                </MetricIcon>
                <MetricContent>
                  <MetricLabel>Badges</MetricLabel>
                  <MetricValue>{securitySnapshotData.badges.length}</MetricValue>
                </MetricContent>
              </MetricCard>
            </Tooltip>
          </MetricsGrid>

          {securitySnapshotLevelKey ? (
            <ProgressSection>
              <ProgressTooltipAnchor>
                <ProgressTooltip>
                  <TooltipHeading>
                    <NamedContentEntryText container={'span'} refKey={securitySnapshotLevelKey} subKey="bodyHeading" />
                  </TooltipHeading>
                  <TooltipBody>
                    <NamedContentEntryText
                      container={'div'}
                      refKey={securitySnapshotLevelKey}
                      subKey="bodyMain"
                      asHtml
                    />
                  </TooltipBody>
                </ProgressTooltip>
                <SteppingProgressBar steps={stepperSteps} value={currentLevelIndex} />
              </ProgressTooltipAnchor>
            </ProgressSection>
          ) : null}
        </StatsCard>
      </HeroLayout>
      {showLeaderboard && <LeaderboardModal isOpen={showLeaderboard} onClose={() => setShowLeaderboard(false)} />}
    </>
  );
};

const HeroLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(132px, 148px) minmax(0, 1fr);
  gap: 10px;
  align-items: stretch;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(84px, 104px) minmax(0, 1fr);
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const AvatarCard = styled(Card)`
  padding: 0;
  gap: 0;
  min-height: 140px;
  overflow: hidden;
  border-radius: 12px;

  @media (max-width: 1024px) {
    min-height: 220px;
  }
`;

const AvatarFrame = styled.div`
  width: 100%;
  height: 100%;
  min-height: 140px;
  border-radius: inherit;
  background: linear-gradient(180deg, #f8f9fc 0%, #eef1f9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  overflow: hidden;
  box-shadow: inset 0 0 0 2px #ffffff;

  @media (max-width: 1024px) {
    min-height: 220px;
  }
`;

const ProfileAvatar = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 6px 12px rgba(12, 23, 54, 0.12);
`;

const StatsCard = styled(Card)`
  padding: 14px 18px 16px;
  gap: 12px;
  min-height: 140px;
  border-radius: 12px;

  @media (max-width: 1024px) {
    padding: 16px;
    min-height: auto;
  }
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;

  @media (max-width: 768px) {
    align-items: flex-start;
  }
`;

const ProfileIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
`;

const Identity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const HeaderTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ProfileName = styled.h3`
  font-size: 16px;
  line-height: 1.25;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.strong};
`;

const ProfileSubtitle = styled.div`
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.normal};
`;

const LevelPill = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ $color }) => $color};
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;

  @media (max-width: 768px) {
    white-space: normal;
  }
`;

const MonthlyScore = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.heading};
  font-size: 12px;
  line-height: 1.4;
  font-weight: 600;

  svg {
    color: #ffb547;
    flex: 0 0 auto;
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 1100px) {
    gap: 8px;
  }

  @media (max-width: 768px) {
    width: 100%;
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled.div`
  border: 1px solid #e1e4ea;
  border-radius: 10px;
  padding: 10px 12px;
  background: ${({ theme }) => theme.colors.panelBackground};
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 72px;
  width: 100%;
  min-width: 0;
  overflow: hidden;

  @media (max-width: 1100px) {
    padding: 10px 8px;
    gap: 8px;
  }
`;

const TooltipInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px 14px;
  font-size: 12px;
  line-height: 1.4;
  text-align: center;
  white-space: nowrap;

  a,
  a * {
    font-size: 12px;
  }
`;

const TooltipActionButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  text-decoration: underline;
  color: inherit;
`;

const MetricIcon = styled.div<{ $color: string }>`
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  margin-top: 0;

  svg {
    display: block;
  }

  @media (max-width: 1100px) {
    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const BadgeHexIcon = styled.svg`
  width: 28px;
  height: 28px;

  path:first-child {
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linejoin: round;
  }

  path:last-child {
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linejoin: round;
  }
`;

const MetricContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
  max-width: 100%;
`;

const MetricLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.normal};
  line-height: 1.2;
  text-align: left;
  max-width: 100%;
`;

const MetricValue = styled.div`
  font-size: 22px;
  line-height: 1;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.strong};
  text-align: left;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProgressSection = styled.div`
  padding-top: 2px;
  margin-top: auto;
`;

const ProgressTooltipAnchor = styled.div`
  position: relative;
  width: 100%;

  &:hover > div:first-child {
    opacity: 1;
    visibility: visible;
    transform: translate(-50%, 0);
  }

  @media (max-width: 768px) {
    &:hover > div:first-child {
      opacity: 0;
      visibility: hidden;
    }
  }
`;

const ProgressTooltip = styled.div`
  position: absolute;
  left: 50%;
  bottom: calc(100% + 14px);
  transform: translate(-50%, 8px);
  width: min(440px, calc(100vw - 72px));
  padding: 16px 18px;
  border-radius: 16px;
  background: #23252f;
  color: #ffffff;
  box-shadow: 0 14px 28px rgba(12, 23, 54, 0.24);
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease,
    visibility 0.2s ease;
  z-index: 3;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 100%;
    transform: translateX(-50%);
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid #23252f;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const TooltipHeading = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: 600;
  line-height: 1.5;
  margin-bottom: 8px;
`;

const TooltipBody = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.normal};
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.88);

  b {
    color: #ffffff;
  }
`;
